// Currency economy: collected coins/diamonds bank into a wallet, spent in the shop on
// persistent upgrades that seed every level's PlayerMods. Pure + unit-tested.
import { TUNER_STEP } from './constants';
import { defaultMods, type PlayerMods } from './powerups';

export interface Wallet {
  coins: number;
  diamonds: number;
}

export interface EconomyState {
  wallet: Wallet;
  /** upgradeId → owned level (0 = not owned) */
  upgrades: Record<string, number>;
  /** keys of collectibles already banked (so replaying a level can't re-farm). */
  collected: string[];
}

export function emptyEconomy(): EconomyState {
  return { wallet: { coins: 0, diamonds: 0 }, upgrades: {}, collected: [] };
}

export interface ShopItem {
  id: string;
  name: string;
  coinCost: number;
  diamondCost: number;
  maxLevel: number; // hard ceiling (toggles = 1)
  /** if set, the item only appears/sells once this zone is fully completed. */
  unlockZone?: string;
  /** if true, the buyable cap grows with completed zones (cap = zonesCleared + 1, ≤ maxLevel). */
  scaleWithZones?: boolean;
}

export const SHOP: ShopItem[] = [
  // Marquee diamond upgrades unlock by clearing a zone — one per zone.
  { id: 'doublejump', name: 'Double Jump', coinCost: 0, diamondCost: 1, maxLevel: 1, unlockZone: 'forest' },
  { id: 'armor', name: 'Armor', coinCost: 0, diamondCost: 1, maxLevel: 1, unlockZone: 'desert' },
  // Stat tuners are coin sinks; their cap rises by one per zone cleared (cost scales too).
  { id: 'jump', name: 'Higher Jump', coinCost: 5, diamondCost: 0, maxLevel: 6, scaleWithZones: true },
  { id: 'dash', name: 'Faster Dash', coinCost: 5, diamondCost: 0, maxLevel: 6, scaleWithZones: true },
  { id: 'run', name: 'Faster Run', coinCost: 5, diamondCost: 0, maxLevel: 6, scaleWithZones: true },
];

/** Is the item available given the set of fully-completed zones? */
export function itemUnlocked(item: ShopItem, completedZones: ReadonlySet<string>): boolean {
  return !item.unlockZone || completedZones.has(item.unlockZone);
}

/** Buyable cap: scaling tuners grow by one per cleared zone (≤ maxLevel); others are fixed. */
export function effectiveMaxLevel(item: ShopItem, zonesCompleted: number): number {
  return item.scaleWithZones ? Math.min(zonesCompleted + 1, item.maxLevel) : item.maxLevel;
}

export function ownedLevel(e: EconomyState, id: string): number {
  return e.upgrades[id] ?? 0;
}

/** Cost of the NEXT level of an item (scales with how many you already own). */
export function itemCost(item: ShopItem, ownedLvl: number): Wallet {
  const mult = ownedLvl + 1;
  return { coins: item.coinCost * mult, diamonds: item.diamondCost * mult };
}

export function canBuy(e: EconomyState, item: ShopItem, zonesCompleted = 0): boolean {
  const lvl = ownedLevel(e, item.id);
  if (lvl >= effectiveMaxLevel(item, zonesCompleted)) return false;
  const c = itemCost(item, lvl);
  return e.wallet.coins >= c.coins && e.wallet.diamonds >= c.diamonds;
}

/** Buy the next level of an item (no-op if unaffordable/at the current cap). */
export function buy(e: EconomyState, item: ShopItem, zonesCompleted = 0): EconomyState {
  if (!canBuy(e, item, zonesCompleted)) return e;
  const lvl = ownedLevel(e, item.id);
  const c = itemCost(item, lvl);
  return {
    ...e,
    wallet: { coins: e.wallet.coins - c.coins, diamonds: e.wallet.diamonds - c.diamonds },
    upgrades: { ...e.upgrades, [item.id]: lvl + 1 },
  };
}

export function addEarnings(e: EconomyState, coins: number, diamonds: number): EconomyState {
  return { ...e, wallet: { coins: e.wallet.coins + coins, diamonds: e.wallet.diamonds + diamonds } };
}

/** Persistent mods granted by purchased upgrades (stacked under in-level pickups). */
export function modsFromUpgrades(e: EconomyState): PlayerMods {
  const m = defaultMods();
  m.doubleJump = ownedLevel(e, 'doublejump') > 0;
  m.armor = ownedLevel(e, 'armor') > 0;
  m.jumpMult = TUNER_STEP ** ownedLevel(e, 'jump');
  m.dashMult = TUNER_STEP ** ownedLevel(e, 'dash');
  m.runMult = TUNER_STEP ** ownedLevel(e, 'run');
  return m;
}

/** Merge two economies (local + remote): max wallet, max owned level per upgrade. */
export function mergeEconomy(a: EconomyState, b: EconomyState): EconomyState {
  const ids = new Set([...Object.keys(a.upgrades), ...Object.keys(b.upgrades)]);
  const upgrades: Record<string, number> = {};
  for (const id of ids) upgrades[id] = Math.max(ownedLevel(a, id), ownedLevel(b, id));
  return {
    wallet: {
      coins: Math.max(a.wallet.coins, b.wallet.coins),
      diamonds: Math.max(a.wallet.diamonds, b.wallet.diamonds),
    },
    upgrades,
    collected: [...new Set([...a.collected, ...b.collected])],
  };
}
